import * as THREE from 'three';

import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import type { POCVersion } from '../useClinic3d';

export type Model3d = {
  load: () => Promise<THREE.Object3D>;
  add: () => void;
  remove: () => void;
};

// In-memory cache for models to avoid reloading
const modelCache: Record<string, Promise<THREE.Object3D>> = {};

// Number of room parts for each version
const ROOM_COUNT = 6;

export function useModel(scene: THREE.Scene, version: POCVersion): Model3d {
  // Container for all loaded model parts
  const modelContainer = new THREE.Group();
  modelContainer.name = `clinic-${version}-container`;

  // Track loading progress
  let loadedParts = 0;

  return { load, add, remove };

  /**
   * Loads all model parts for the specified version
   */
  function load(): Promise<THREE.Object3D> {
    // Reset progress
    if (typeof window !== 'undefined') {
      window.Clinic3d.LoadingProgress.GlbModel = 0;
    }

    // Create promises for all room parts
    const loadPromises: Promise<THREE.Object3D>[] = [];

    for (let i = 1; i <= ROOM_COUNT; i++) {
      const roomUrl = `/models/${version}/room-${i}.glb`;
      loadPromises.push(loadModelPart(roomUrl, i));
    }

    // Wait for all parts to load
    return Promise.all(loadPromises)
      .then((parts) => {
        // Add all parts to the container
        parts.forEach((part) => {
          modelContainer.add(part);
        });

        // Ensure 100% progress when done
        if (typeof window !== 'undefined') {
          window.Clinic3d.LoadingProgress.GlbModel = 100;
        }

        return modelContainer;
      })
      .catch((error) => {
        console.error('Error loading model parts:', error);
        throw error;
      });
  }

  /**
   * Loads a single model part with caching
   */
  function loadModelPart(
    url: string,
    partIndex: number,
  ): Promise<THREE.Object3D> {
    // Check cache first
    if (url in modelCache) {
      return modelCache[url].then((originalModel) => {
        // Clone the model to avoid conflicts
        const clonedModel = originalModel.clone();
        updateProgress();
        return clonedModel;
      });
    }

    // Create and configure loaders
    const gltfLoader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath('/scripts/draco/');
    gltfLoader.setDRACOLoader(dracoLoader);

    // Create loading promise and store in cache
    const loadingPromise = new Promise<THREE.Object3D>((resolve, reject) => {
      // Try to get from browser cache first
      caches
        .open('glb-assets')
        .then((cache) => {
          return cache.match(url).then((response) => {
            if (response) {
              // Found in cache
              return response;
            } else {
              // Not in cache, fetch it
              return fetch(url).then((fetchResponse) => {
                if (!fetchResponse.ok) {
                  throw new Error(
                    `Failed to fetch GLB: ${fetchResponse.statusText}`,
                  );
                }
                // Store in cache and return the response
                cache.put(url, fetchResponse.clone());
                return fetchResponse;
              });
            }
          });
        })
        .then((response) => response.arrayBuffer())
        .then((arrayBuffer) => {
          // Parse the model from ArrayBuffer
          gltfLoader.parse(
            arrayBuffer,
            '',
            (gltf) => {
              updateProgress();
              resolve(gltf.scene);
            },
            (error) => {
              console.error(
                `Error parsing GLB model part ${partIndex}:`,
                error,
              );
              reject(error);
            },
          );
        })
        .catch((error) => {
          console.error(
            `Error loading or caching GLB model part ${partIndex}:`,
            error,
          );
          reject(error);
        });
    });

    // Store in memory cache and return
    modelCache[url] = loadingPromise;
    return loadingPromise;
  }

  /**
   * Updates the overall loading progress
   */
  function updateProgress() {
    loadedParts++;
    // Update global loading progress
    if (typeof window !== 'undefined') {
      window.Clinic3d.LoadingProgress.GlbModel = Math.floor(
        (loadedParts / ROOM_COUNT) * 100,
      );
    }
  }

  /**
   * Adds the complete model to the scene
   */
  function add() {
    if (modelContainer.children.length === 0) {
      console.warn('Attempting to add model before loading');
      return;
    }
    modelContainer.name = 'clinic-model';
    modelContainer.position.x = -0.015;
    scene.add(modelContainer);
  }

  /**
   * Removes the model from the scene
   */
  function remove() {
    if (modelContainer.parent) {
      scene.remove(modelContainer);
    }
  }
}
